import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { SimpleMatDataSource } from '../../../common/providers/simple-mat-data-source';
import { BreadcrumbProvider, Crumb } from '../../../breadcrumb/providers/breadcrumb-provider';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { UsersProvider } from '../../../users/providers/users-provider';
import { DocumentService } from '../../providers/document.service';
import { UploadDialogComponent } from '../../components/dialog/dialog.component';
import { DownloaderHelper } from '../../helpers/downloader.helper';
import { BytesPipe, NgMathPipesModule } from 'ngx-pipes';
import { PreviewDialogComponent } from '../../components/preview-dialog/preview-dialog.component';
import { DocumentRights } from '../../providers/document-rights';
import { CurrentUserRolesProvider } from '../../../organizations/providers/current-user-roles-provider';
import { CurrentUser } from '../../../common/providers/current-user';
import { ConfigProvider } from "../../../config-provider.service";
import { Title } from '@angular/platform-browser';
import { Subscription } from 'rxjs';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { RenameDocumentDialogComponent } from '../../components/rename-dialog/rename-dialog';
import { Organization } from "../../../common/models/organization";
import { Athlete } from "../../../common/models/athlete";
import { User } from "../../../common/models/user";
import { UtilsProvider } from "../../../common/providers/utils-provider.service";
import { ModuleMenu } from "../../../common/models/menu";
import { Paginator } from "../../../common/models/paginator";
import { Document } from "../../../common/models/document";
import { ApiBaseService } from "../../../common/providers/api-base-service";
import { OrganizationRole } from "../../../common/models/organization-role";
import { ConfirmDialogComponent } from "../../../common/components/confirm-dialog/confirm-dialog";
import { FlexLayoutModule } from "@angular/flex-layout";
import { NgIf } from "@angular/common";
import { MatIcon } from "@angular/material/icon";
import { MatMenuItem, MatMenuModule } from "@angular/material/menu";
import { MatIconButton } from "@angular/material/button";
import { MatHeaderCell, MatTableModule } from "@angular/material/table";
import { AppDatePipe } from "../../../common/pipes/app-date";
import { MatSort, MatSortModule } from "@angular/material/sort";
import { MatFormField, MatFormFieldModule, MatSuffix } from "@angular/material/form-field";
import { MatInput } from "@angular/material/input";
import { ModuleContentComponent } from "../../../common/components/module-content/module-content";
import { Error401Component } from "../../../common/components/error-401/error-401";
import { Error404Component } from "../../../common/components/error-404/error-404";
import { SideMenuComponent } from "../../../common/components/side-menu/side-menu";
import { AthleteSideMenuComponent } from "../../../athletes/components/side-menu/side-menu";
import { HeaderComponent } from "../../../common/components/header-elitment/header-elitment";
import { PageContentComponent } from "../../../common/components/page-content/page-content";
import { AthleteCoach } from "../../../common/models/athlete-coach";
import { Coach } from "../../../common/models/coach";

@Component({
  selector: 'app-document-list-container',
  templateUrl: './document-list-container.component.html',
  styleUrls: ['./document-list-container.scss'],
  standalone: true,
  imports: [
    TranslateModule,
    MatPaginator,
    FlexLayoutModule,
    NgIf,
    MatIcon,
    MatMenuModule,
    MatIconButton,
    MatTableModule,
    AppDatePipe,
    NgMathPipesModule,
    MatSortModule,
    MatSuffix,
    MatInput,
    MatFormFieldModule,
    ModuleContentComponent,
    Error401Component,
    Error404Component,
    SideMenuComponent,
    AthleteSideMenuComponent,
    HeaderComponent,
    PageContentComponent
  ],
  providers: [BytesPipe]
})
export class DocumentListContainerComponent implements OnInit, AfterViewInit, OnDestroy {
    public isLoading = true;
    public is401 = false;
    public is404 = false;

    public dataSource: SimpleMatDataSource = new SimpleMatDataSource([]);
    public columns: string[] = ['name', 'createDate', 'size', 'action'];

    public organization: Organization = {} as Organization;
    public athleteUser: User = {} as User;

    public paginatedResults: Paginator =  {} as Paginator;
    public pageSize: number = Document.NUM_ITEMS;
    public currentPage = 0;
    public totalSize = 0;
    public pageTitle: string | null = null;

    public mainActions: ModuleMenu[] = [];

    public docRights: DocumentRights = new DocumentRights(this.utils);

    @ViewChild(MatPaginator, {static: false}) public paginator: MatPaginator | undefined;

    private subscriptions: Subscription[] = [];

    constructor(
        public conf: ConfigProvider,
        private title: Title,
        private documentProvider: DocumentService,
        private route: ActivatedRoute,
        private breadcrumbProvider: BreadcrumbProvider,
        private translator: TranslateService,
        private userProvider: UsersProvider,
        private dialog: MatDialog,
        private previewDialog: MatDialog,
        private toaster: MatSnackBar,
        private utils: UtilsProvider,
        private currentUserRoles: CurrentUserRolesProvider,
        private currentUser: CurrentUser,
        private api: ApiBaseService,
    ) {
    }

    public ngOnInit(): void {
        this.subscriptions.push(
            this.route.data.subscribe((data: any) => { //{ organization: Organization; athlete: Athlete; user: User }
                this.organization = data.organization;
                this.athleteUser = data.athlete;

                Promise.all([this.loadPaginatedData()]).then(() => {

                    this.docRights.evaluateScreenRights({
                        rolesIds: this.currentUserRoles.getRolesIds(this.organization.id!)
                    });

                    if (!this.docRights.LIST) {
                        this.is401 = true;
                        return;
                    }

                    if (this.docRights.ADD) {
                        this.mainActions = [{
                            title: 'Add',
                            icon: 'add',
                            url: null,
                            onClick: () => this.openUploadDialog()
                        }];
                    }

                    this.createPageTitle();
                    this.createBreadcrumb();
                    this.isLoading = false;
                });
            })
        );
    }

    public ngAfterViewInit(): void {
        this.dataSource.paginator = this.paginator!;
    }

    public ngOnDestroy(): void {
        this.subscriptions.forEach((subscription) => subscription.unsubscribe());
    }

    public loadPaginatedData(page = 0, query = ''): Promise<any> {
        return new Promise((resolve, reject) => {

            this.subscriptions.push(
                this.documentProvider.getByAthletePaginated(this.organization, this.athleteUser.athlete!, page, query).subscribe((paginator: any) => {
                    this.paginatedResults = paginator;
                    this.currentPage = this.paginatedResults.current_page - 1;
                    this.pageSize = this.paginatedResults.per_page;
                    this.totalSize = this.paginatedResults.total;

                    this.dataSource.replaceAllWith(this.paginatedResults.data);

                    this.paginatedResults = paginator;
                    this.currentPage = this.paginatedResults.current_page - 1;
                    this.pageSize = this.paginatedResults.per_page;
                    this.totalSize = this.paginatedResults.total;

                    for (let i = 0; i < this.paginatedResults.data.length; i++) {
                        this.docRights.evaluateItemRight({
                            user: this.currentUser.user!,
                            organizationId: this.organization.id!,
                            document: this.paginatedResults.data[i],
                            rolesIds: this.currentUserRoles.getRolesIds(this.organization.id!),
                            athleteCoachRelations:  this.athleteUser.athlete!.coaches?.map(
                                (c: Coach) => {
                                    return new AthleteCoach({
                                        athleteId: this.athleteUser.athlete!.id!,
                                        coachId: c.id!
                                    });
                                }
                            ) ?? [],
                            athleteMemberships: this.athleteUser.organizationMemberships ?? [],
                            coaches: this.athleteUser.athlete!.coaches ?? [],
                            athletes: [this.athleteUser.athlete!]
                        });
                        this.paginatedResults.data[i].rights = this.docRights.getItemRightsList();
                    }

                    this.dataSource.replaceAllWith(this.paginatedResults.data);

                    resolve(null);
                })
            );
        });
    }

    /**
     * Perform a new request for the requested page
     *
     * @param event
     */
    public onPaginateChange(event: any) {
        this.loadPaginatedData(event.pageIndex + 1);
    }

    public onFilterKeyup(filter: string): void {
        this.loadPaginatedData(0, filter);
    }

    public onRenameClicked(row:any) {
        const dialog: MatDialogRef<RenameDocumentDialogComponent> = this.dialog.open(RenameDocumentDialogComponent, {
            data: {
                name: row.originalName,
                organization: this.organization,
                athlete: this.athleteUser.athlete!,
                documentId: row.id
            }
        });
        dialog.afterClosed().subscribe((document) => {
            this.loadPaginatedData(this.currentPage + 1);
        });
    }

    public onDeleteClicked(row: any) {
        const dialog: MatDialogRef<ConfirmDialogComponent> = this.dialog.open(ConfirmDialogComponent, {
            data: {
                confirmText: 'DocumentConfirmDelete',
                confirmActionText: 'Delete',
                confirmColor: 'primary'
            }
        });
        dialog.afterClosed().subscribe((confirmed: boolean) => {
            if (confirmed) {
                this.subscriptions.push(
                    this.documentProvider.deleteById(this.organization, this.athleteUser.athlete!, row.id).subscribe(
                        (data) => {
                            this.toaster.open(this.translator.instant('Document_Deleted'), '', {duration: 5000});
                        },
                        (err) => {
                            this.toaster.open(err.error.message, '', {duration: 5000});
                        },
                        () => {
                            this.loadPaginatedData(this.currentPage + 1);
                        })
                );
            }
        });
    }

    public openUploadDialog() {
        const dialogRef: MatDialogRef<UploadDialogComponent> = this.dialog.open(UploadDialogComponent, {
            height: '50%',
            data: {
                organization: this.organization,
                athlete: this.athleteUser.athlete!,
            }
        });
        this.subscriptions.push(
            dialogRef.componentInstance.filesUploaded.subscribe(() => {
                this.loadPaginatedData(this.currentPage + 1);
            })
        );
    }

    public onPreviewClicked(d: Document) {
        const dialogRef: MatDialogRef<PreviewDialogComponent> = this.previewDialog.open(PreviewDialogComponent, {
            height: '50%',
            data: {
                organization: this.organization,
                athlete: this.athleteUser.athlete!,
                document: d,
            }
        });
    }

    public download(d: Document) {
        this.subscriptions.push(
            this.documentProvider.download(this.organization, this.athleteUser.athlete!, d).subscribe((response: Response) => {
                DownloaderHelper.forceDownload(response as any as Blob, d.originalName?? 'document');
            })
        );
    }

    private createPageTitle() {
        this.pageTitle = this.translator.instant('Documents');
        this.title.setTitle(this.pageTitle + ' | ' + this.conf.platformName);
    }

    private createBreadcrumb() {

        const crumbs: Crumb[] = [];
        if (!this.currentUserRoles.have(OrganizationRole.ID_ATHLETE, this.organization.id!)) {
            crumbs.push({
                shortTitle: 'Athletes',
                title: 'Athletes',
                url: '/' + this.organization!.code + '/athletes'
            });
        }
        crumbs.push({
            shortTitle: `${this.athleteUser.firstName?.substr(0, 1)}. ${this.athleteUser.lastName}`,
            title: `${this.athleteUser.firstName} ${this.athleteUser.lastName}`,
            url: `/${this.organization!.code}/athletes/${this.athleteUser.id}`
        });
        crumbs.push({
            shortTitle: 'Documents',
            title: 'DocumentsAll'
        });
        this.breadcrumbProvider.setDefaultCrumbs(crumbs);
    }
}
